import React, { FC, useMemo, useState } from "react";
import { IButtonItemProps, LinearProgress, useDialogShadow } from "@evergis/ui";
import { GeometryType, ResourceInfoDc } from "@evergis/api";
import { useTranslation } from "react-i18next";
import { isSystemAttribute } from "../../../../../evergis/core/attributes/attributes";

import { useAccessControlList, useAclUpdate } from "../../../../../hooks/acl/useAccessControlListUpdates";
import { useNotification } from "../../../../../hooks/notifications/useNotification";
import { useBulkOperationsToggle } from "../../../../../hooks/useBulkOperationsToggle";
import { usePropAsState } from "../../../../../hooks/usePropAsState";
import { useTrigger } from "../../../../../hooks/useTrigger";
import { featuresCount } from "../../../../../resources/features";
import { formatDate } from "../../../../../utils/date";
import { numberWithSpaces } from "../../../../../utils/formatNumber";
import { Attributes } from "../../../DialogComponents/Attributes/Attributes";
import { LayerReferences } from "../../../DialogComponents/Attributes/LayerReferences/LayerReferences";
// eslint-disable-next-line max-len
import { useLayerReferencesContext } from "../../../DialogComponents/Attributes/LayerReferences/LayerReferencesProvider";
// eslint-disable-next-line max-len
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { TableSourceDescription } from "../../../DialogComponents/TableSourceDescription";
import { DataField } from "../../../styled";
import { LayerAttribute, LayerAttributeItem } from "./LayerAttributeItem";
import { LayerAttributesHeader } from "./LayerAttributesHeader";
import { LayerAttributesContainer, MetaRow, MetaRowItem } from "./styled";
import { useDialogReset } from "../../../../../hooks/useDialogs";
import { getMetaFromResource, useResourceMeta } from "../../../../../hooks/useResourceMeta";
import { useFirstVisibleAttribute } from "../../hooks/useFirstVisibleAttribute";
import { useLayerAttributes } from "../../hooks/useLayerAttributes";
import { useLayerInfoWithUpdate } from "../../hooks/useLayerInfoWithUpdate";
import { useUpdateLayer } from "../../hooks/useUpdateLayer";

interface PostgresLayerSettingsProps {
  dialogShadow: ReturnType<typeof useDialogShadow>;
}

// eslint-disable-next-line max-lines-per-function
export const PostgresLayerSettings: FC<PostgresLayerSettingsProps> = ({ dialogShadow }) => {
  const { t } = useTranslation("dialogs");
  const [{ layer, table, previewUrl, loading }] = useLayerInfoWithUpdate();
  const closeDialog = useDialogReset();
  const [meta, updateMeta] = useResourceMeta(getMetaFromResource(layer as ResourceInfoDc, previewUrl));
  const [displayField, setDisplayField] = usePropAsState(layer.titleAttribute || layer.idAttribute);
  const [{ publicAccessUpdates }] = useAccessControlList(layer.acl);
  const [aclUpdate, { data: aclUpdated, loading: updating }] = useAclUpdate(publicAccessUpdates);
  const [attributes, { updateAttribute, setAttributes }] = useLayerAttributes(layer, table.columns);
  const { shadows, setScrollContainer } = dialogShadow;
  const firstVisibleAttribute = useFirstVisibleAttribute(attributes);
  const attributesTypes = useMemo(
    (): IButtonItemProps[] => [
      {
        label: t("editResource.basic"),
      },
      {
        label: t("editResource.related"),
      },
    ],
    [t],
  );
  const [selectedType, setSelectedType] = useState<IButtonItemProps>(attributesTypes[0]);
  const isBasicAttributes = selectedType === attributesTypes[0];
  const isLayerReferenceAttributes = selectedType === attributesTypes[1];

  const [allAttributes, toggleAllAttributes] = useBulkOperationsToggle<LayerAttribute>({
    items: attributes,
    setItems: setAttributes,
    column: "isDisplayed",
    excludeItem: isSystemAttribute,
  });

  const { layerReferences } = useLayerReferencesContext();

  const [updateLayer, { loading: updateLoading, layerUpdated, error }] = useUpdateLayer({
    layer,
    attributes,
    displayField,
    meta,
    layerReferences,
  });

  const {
    data: featuresCountData,
    error: featuresCountError,
    loading: featuresCountLoading,
  } = featuresCount.use({
    input: { name: layer?.name || null },
    waitInput: true,
  });

  useNotification(!loading && error, {
    error: true,
    title: t("editResource.errorUpdatingLayer"),
    description: `${meta.alias}. ${t("editResource.tryAgainOrCheckData")}`,
  });

  useNotification(!loading && layerUpdated, {
    success: true,
    title: t("editResource.layerUpdated"),
    description: meta.alias,
  });

  useNotification(!featuresCountLoading && featuresCountError, {
    error: true,
    title: t("editResource.objectsCountFetchError"),
    description: featuresCountError?.message,
  });

  useTrigger(layerUpdated && !updateLoading, aclUpdate);
  useTrigger(aclUpdated && !updating, closeDialog);

  return (
    <>
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          <ProjectMetaBlock>
            <ResourceMeta meta={meta} updateMeta={updateMeta} loading={loading}>
              <DataField column>
                <TableSourceDescription
                  tableName={table.alias || table.name}
                  layerName={meta.alias}
                  geometryType={(layer.geometryType || table.geometryType) as GeometryType}
                />
                <MetaRow>
                  <MetaRowItem
                    field={t("access.objects")}
                    value={featuresCountLoading ? <LinearProgress /> : numberWithSpaces(featuresCountData)}
                  />
                  <MetaRowItem field={t("editResource.updated")} value={formatDate(layer.changedDate)} />
                </MetaRow>
              </DataField>
            </ResourceMeta>
          </ProjectMetaBlock>
        </RestrictedWidthContainer>

        <LayerAttributesContainer>
          <LayerAttributesHeader
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            attributesTypes={attributesTypes}
          />
          {isBasicAttributes && (
            <Attributes
              attributes={attributes}
              setAttributes={setAttributes}
              loading={loading}
              allAttributes={allAttributes}
              toggleAllAttributes={toggleAllAttributes}
              showTitle={false}
              showDescription={false}
              isEdit
            >
              {(attribute: LayerAttribute) => (
                <LayerAttributeItem
                  attribute={attribute}
                  firstVisibleAttribute={firstVisibleAttribute}
                  displayField={displayField}
                  updateAttribute={updateAttribute}
                  setDisplayField={setDisplayField}
                  geometryType={(layer.geometryType || table.geometryType) as GeometryType}
                  layerReferences={layerReferences}
                />
              )}
            </Attributes>
          )}
          {isLayerReferenceAttributes && <LayerReferences />}
        </LayerAttributesContainer>
      </DialogContent>
      <DialogActions
        applyDisabled={!meta.alias}
        onApply={updateLayer}
        displayShadow={shadows.bottom}
        isWaiting={updateLoading}
      />
    </>
  );
};
